import { trackEvent, AnalyticsEvents } from '../analytics/instance';
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { useUserStore } from '../store/userStore';
import { User, CompetencyMap } from '../types';
import { resolveGradeDisplay, CompetencyMapLike } from '../utils/gradeDisplay';

// Хуки для пользователей
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: async (): Promise<User[]> => {
      return await api('/users');
    },
  });
};

// Лёгкий справочник людей (id/name/position/avatar), доступный любому
// авторизованному пользователю. В отличие от useUsers (GET /users, только
// для админов), используется в пикерах для обычных юзеров — например, выбор
// желаемых ревьюеров в perf-review.
/** Справочник коллег; `module` — только те, кто пользуется разделом (пикеры участников). */
export const useUsersDirectory = (module?: string) => {
  return useQuery({
    queryKey: ['users', 'directory', module ?? 'all'],
    queryFn: async (): Promise<User[]> => {
      return await api(module ? `/users/directory?module=${module}` : '/users/directory');
    },
  });
};

export const useUserCompetencies = (userId: string) => {
  return useQuery({
    queryKey: ['users', userId, 'competencies'],
    queryFn: async (): Promise<
      Array<{
        competencyId: string;
        acquiredDate: string;
        approvedBy: string;
      }>
    > => {
      return await api(`/users/${userId}/competencies`);
    },
    enabled: !!userId,
  });
};

export const useUser = (id: string) => {
  return useQuery({
    queryKey: ['users', id],
    queryFn: async (): Promise<User> => {
      return await api(`/users/${id}`);
    },
    enabled: !!id,
  });
};

/** Карта компетенций по профессии пользователя: `me` — своя, иначе — того, чей профиль открыт. */
export const useUserCompetencyMap = (userId = 'me') => {
  const { data: competencyMap, ...queryResult } = useQuery({
    queryKey: ['competency-maps', 'user', userId],
    queryFn: async (): Promise<CompetencyMap> => {
      return await api(`/competency-maps/user/${userId}`);
    },
  });

  // Получаем информацию о пользователе
  const user = useUserStore((s) => s.user);

  // Вычисляем информацию о прогрессе
  const progressInfo = React.useMemo(() => {
    if (!competencyMap?.grades || !user?.grade) {
      return {
        progressToNextGrade: 0,
        currentGrade: undefined,
        nextGrade: undefined,
        acquiredCount: 0,
        totalCount: 0,
      };
    }

    let progressToNextGrade = 0;
    const currentGradeIndex = competencyMap.grades.findIndex(
      (grade) => grade._id === user?.grade?._id,
    );
    const currentGradeObj = competencyMap.grades[currentGradeIndex];
    const nextGradeObj = competencyMap.grades[currentGradeIndex + 1];

    if (nextGradeObj?.competencies) {
      const userCompetencyIds = new Set(user.competencies?.map((uc) => uc.competencyId) || []);
      const totalCompetencies = nextGradeObj.competencies.length;
      const acquiredCompetencies = nextGradeObj.competencies.filter((comp) =>
        userCompetencyIds.has(comp._id),
      ).length;
      progressToNextGrade =
        totalCompetencies > 0 ? Math.round((acquiredCompetencies / totalCompetencies) * 100) : 0;
    }

    const allCompetencies = [
      ...(currentGradeObj?.competencies || []),
      ...(nextGradeObj?.competencies || []),
    ];
    const allCompetencyIds = allCompetencies.map((c) => c._id);
    const userCompetencyIds = (user.competencies || []).map((c) => c.competencyId);
    const acquiredCount = allCompetencyIds.filter((id) => userCompetencyIds.includes(id)).length;
    const totalCount = allCompetencyIds.length;

    return {
      progressToNextGrade,
      currentGrade: currentGradeObj,
      nextGrade: nextGradeObj,
      acquiredCount,
      totalCount,
    };
  }, [competencyMap, user]);

  // Additive: drives the dashboard "Grade and roles" block, which falls back to
  // current-grade mastery at the top grade. Existing fields above are untouched
  // so other consumers (/grades, profile) keep their behaviour.
  const gradeDisplay = React.useMemo(
    () =>
      resolveGradeDisplay(
        competencyMap as unknown as CompetencyMapLike,
        (user?.competencies || []).map((c) => c.competencyId),
        user?.grade?._id,
      ),
    [competencyMap, user],
  );

  return {
    ...queryResult,
    data: competencyMap,
    ...progressInfo,
    gradeDisplay,
  };
};

export const useUserRoleDistribution = () => {
  return useQuery({
    queryKey: ['user', 'role-distribution'],
    queryFn: async (): Promise<Array<{ name: string; value: number; color: string }>> => {
      return await api('/users/me/role-distribution');
    },
  });
};

export const useUpdateUserRoleDistribution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleDistribution: Array<{ name: string; value: number; color: string }>) => {
      return await api('/users/me/role-distribution', {
        method: 'PUT',
        body: { roleDistribution },
      });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'role-distribution'] });
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
      // Сайдбар и дашборд читают пользователя из стора — обновляем и его.
      await useUserStore.getState().checkSession();
    },
  });
};

// Хуки для мутаций пользователей
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: FormData) => {
      return await api('/users', {
        method: 'POST',
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormData }) => {
      return await api(`/users/${id}`, {
        method: 'PATCH',
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await api(`/users/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

// Хук для получения пользователей с определенной компетенцией
export const useUsersWithCompetency = (competencyId: string) => {
  return useQuery({
    queryKey: ['users', 'with-competency', competencyId],
    queryFn: async (): Promise<User[]> => {
      return await api(`/users/with-competency/${competencyId}`);
    },
    enabled: !!competencyId,
    staleTime: 5 * 60 * 1000, // Кешируем на 5 минут
  });
};

// Side-effects shared by grant + revoke: invalidate every queryKey shape the
// codebase uses for "this user" and sync the Zustand store when the target is
// the logged-in user (progress widgets read from the store, not from query
// cache).
function applyGrantRevokeSideEffects(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  updatedUser: User,
) {
  queryClient.invalidateQueries({ queryKey: ['users', userId] });
  queryClient.invalidateQueries({ queryKey: ['users', userId, 'competencies'] });
  queryClient.invalidateQueries({ queryKey: ['users'] });
  // profile/index.tsx still uses a singular ['user', id] key.
  queryClient.invalidateQueries({ queryKey: ['user', userId] });

  const storeUser = useUserStore.getState().user;
  if (storeUser && storeUser._id === userId) {
    useUserStore.getState().setUser(updatedUser);
  }
}

export const useGrantCompetency = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ competencyId, reason }: { competencyId: string; reason?: string }) => {
      return (await api(`/users/${userId}/competencies`, {
        method: 'POST',
        body: { competencyId, reason },
      })) as User;
    },
    onSuccess: (updatedUser) => {
      trackEvent(AnalyticsEvents.COMPETENCY_ASSIGNED, { source: 'grant' });
      applyGrantRevokeSideEffects(queryClient, userId, updatedUser);
    },
  });
};

export const useRevokeCompetency = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (competencyId: string) => {
      return (await api(`/users/${userId}/competencies/${competencyId}`, {
        method: 'DELETE',
      })) as User;
    },
    onSuccess: (updatedUser) => {
      trackEvent(AnalyticsEvents.COMPETENCY_REVOKED);
      applyGrantRevokeSideEffects(queryClient, userId, updatedUser);
    },
  });
};
